import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

function clamp(value, min, max) {
    return Math.min(Math.max(Number(value) || 0, min), max);
}

export default function SemiGaugeChart({ rows = [], xKey, yAxisKeys = [], theme = {}, title, onExposeLegend }) {
    const keys = Array.isArray(yAxisKeys) ? yAxisKeys : [];
    const valueMode = theme?.valueMode || 'last'; // 'last' | 'sum' | 'avg'
    const maxValue = typeof theme?.max === 'number' ? theme.max : 100;
    const chartHeight = theme?.height ?? theme?.chartHeight ?? '100%';
    const chartWidth = theme?.width ?? theme?.chartWidth ?? '100%';

    // Build multi segments: prefer actual data (multiple yKeys). Theme segments act as defaults (label/color).
    const segments = useMemo(() => {
        const themeSegs = Array.isArray(theme?.segments) ? theme.segments : [];
        const computeVal = (key) => {
            if (!Array.isArray(rows) || rows.length === 0) return 0;
            if (valueMode === 'sum') return rows.reduce((a, r) => a + (Number(r?.[key]) || 0), 0);
            if (valueMode === 'avg') {
                const t = rows.reduce((a, r) => a + (Number(r?.[key]) || 0), 0);
                return rows.length ? t / rows.length : 0;
            }
            const last = rows[rows.length - 1];
            return Number(last?.[key]) || 0;
        };
        if (keys.length > 1) {
            return keys.map((k, i) => ({
                label: String(themeSegs?.[i]?.label ?? k),
                value: computeVal(k), // value always from data when multiple keys
                color: themeSegs?.[i]?.color ?? theme?.colors?.[i]
            }));
        }
        if (themeSegs.length > 1) {
            // Theme-driven multi when data has only one key/value
            return themeSegs.map(s => ({ label: String(s.label ?? ''), value: Number(s.value) || 0, color: s.color }));
        }
        return null;
    }, [keys, rows, valueMode, theme?.segments, theme?.colors]);

    const isMulti = useMemo(() => Boolean(segments && segments.length > 1), [segments]);
    const multiLabels = useMemo(() => (segments && isMulti ? segments.map(s => s.label) : []), [segments, isMulti]);
    const multiSeries = useMemo(() => (segments && isMulti ? segments.map(s => s.value) : []), [segments, isMulti]);
    const multiColors = useMemo(() => (segments && isMulti ? segments.map(s => s.color).filter(Boolean) : []), [segments, isMulti]);
    const multiOptions = useMemo(() => ({
        chart: { type: 'donut', sparkline: { enabled: true } },
        labels: multiLabels,
        colors: multiColors.length ? multiColors : (Array.isArray(theme?.colors) ? theme.colors : undefined),
        plotOptions: {
            pie: {
                startAngle: typeof theme?.startAngle === 'number' ? theme.startAngle : -90,
                endAngle: typeof theme?.endAngle === 'number' ? theme.endAngle : 90,
                donut: { size: theme?.hollowSize || '65%' }
            }
        },
        legend: {
            show: theme?.legend === false ? false : (theme?.legend === true ? true : (typeof theme?.legend?.show === 'boolean' ? theme.legend.show : Boolean(theme?.legend))),
            position: typeof theme?.legend?.position === 'string' ? theme.legend.position : 'bottom',
            horizontalAlign: typeof theme?.legend?.horizontalAlign === 'string' ? theme.legend.horizontalAlign : 'center'
        },
        dataLabels: { enabled: false },
    }), [multiLabels, multiColors, theme?.colors, theme?.startAngle, theme?.endAngle, theme?.hollowSize, theme?.legend]);

    const yKey = keys[0];
    const value = useMemo(() => {
        if (typeof theme?.value === 'number') return theme.value;
        if (!yKey || !Array.isArray(rows) || rows.length === 0) return 0;
        if (valueMode === 'sum') return rows.reduce((acc, r) => acc + (Number(r?.[yKey]) || 0), 0);
        if (valueMode === 'avg') {
            const total = rows.reduce((acc, r) => acc + (Number(r?.[yKey]) || 0), 0);
            return rows.length ? total / rows.length : 0;
        }
        const last = rows[rows.length - 1];
        return Number(last?.[yKey]) || 0;
    }, [rows, yKey, valueMode, theme?.value]);

    const series = useMemo(() => [clamp((value / (maxValue || 1)) * 100, 0, 100)], [value, maxValue]);

    const options = useMemo(() => ({
        chart: { type: 'radialBar', sparkline: { enabled: true } },
        colors: Array.isArray(theme?.colors) && theme.colors.length ? theme.colors : [theme?.gaugeColor || '#4F46E5'],
        plotOptions: {
            radialBar: {
                startAngle: typeof theme?.startAngle === 'number' ? theme.startAngle : -90,
                endAngle: typeof theme?.endAngle === 'number' ? theme.endAngle : 90,
                hollow: { size: theme?.hollowSize || '65%' },
                track: {
                    background: theme?.trackBackground || '#e5e7eb',
                    strokeWidth: theme?.trackStrokeWidth || '100%',
                },
                dataLabels: {
                    name: { show: Boolean(theme?.showName !== false), color: '#64748b', offsetY: -2 },
                    value: {
                        show: Boolean(theme?.showValue !== false),
                        color: '#111827',
                        fontSize: '16px',
                        formatter: (val) => `${Math.round(val)}%`
                    }
                }
            }
        },
        labels: [theme?.label || title || ""],
        legend: {
            show: theme?.legend === false ? false : (theme?.legend === true ? true : (typeof theme?.legend?.show === 'boolean' ? theme.legend.show : false)),
            position: typeof theme?.legend?.position === 'string' ? theme.legend.position : 'bottom',
            horizontalAlign: typeof theme?.legend?.horizontalAlign === 'string' ? theme.legend.horizontalAlign : 'center'
        }
    }), [theme, title]);

    // Expose legend items after render commit
    const exposedItems = useMemo(() => {
        if (isMulti) {
            const colors = (multiOptions && Array.isArray(multiOptions.colors) && multiOptions.colors.length)
                ? multiOptions.colors
                : (Array.isArray(theme?.colors) ? theme.colors : []);
            return (multiLabels || []).map((label, i) => ({ label: String(label), color: colors?.[i] }));
        }
        const firstColor = (Array.isArray(theme?.colors) && theme.colors.length) ? theme.colors[0] : (theme?.gaugeColor || '#4F46E5');
        return [{ label: String(theme?.label || title || 'Value'), color: firstColor }];
    }, [isMulti, multiLabels, multiOptions, theme?.colors, theme?.gaugeColor, theme?.label, title]);

    React.useEffect(() => {
        if (typeof onExposeLegend === 'function') {
            onExposeLegend(exposedItems);
        }
    }, [onExposeLegend, JSON.stringify(exposedItems)]);

    return isMulti
        ? (<Chart type="donut" height={chartHeight} width={chartWidth} series={multiSeries} options={multiOptions} />)
        : (
            <Chart
                type="radialBar"
                height={chartHeight}
                width={chartWidth}
                series={series}
                options={options}
            />
        );
}


